import React from 'react';
import { useLocation } from 'react-router-dom';
import { Mail, Phone, Instagram, MapPin, Clock } from 'lucide-react';
import { BRAND, STOCK } from '@/lib/constants';
import Hero from '@/components/Hero';
import SectionHeader from '@/components/SectionHeader';
import Reveal from '@/components/Reveal';
import ContactForm from '@/components/forms/ContactForm';
import Seo from '@/components/Seo';

function useQuerySubject() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  return params.get('subject') || '';
}

export default function Contact() {
  const subject = useQuerySubject();
  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact Florida SunKissed Mangoes"
        description="Reach Florida SunKissed Mangoes for box orders, local pickup, local delivery, wholesale inquiries, or grower partnerships. Email, phone, or use our contact form."
        keywords="contact Florida SunKissed Mangoes, mango wholesale contact, mango box orders, Florida mango inquiry"
        ogImage={STOCK.mango_plated}
      />

      <Hero
        testId="contact-hero"
        eyebrow="Contact"
        title={<>Let’s talk <span className="text-[hsl(var(--c-mango-red))]">mangoes</span>.</>}
        subtitle="Box orders, local pickup, local delivery, wholesale inquiries, and grower partnerships — we’re here. Reach out by phone, email, or the form below."
        block="teal"
        bgImage={STOCK.mango_plated}
        rightImage={STOCK.mango_cut}
        imageBlock="yellow"
        rightAccent="orange"
        leftAccent="red"
        ctas={[
          { label: 'Email Us', href: `mailto:${BRAND.contact.email}`, variant: 'yellow', testId: 'contact-hero-email' },
          { label: 'Call Us', href: BRAND.contact.phoneHref, variant: 'orange', testId: 'contact-hero-call' },
        ]}
      />

      <section className="block-cream border-t-2 border-[hsl(var(--c-ink))]">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-10 lg:px-16 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border-2 border-[hsl(var(--c-ink))]">
            <Reveal className="lg:col-span-5 bg-[hsl(var(--c-ink))] text-[hsl(var(--c-cream))]" variant="left">
              <div className="p-7 sm:p-10 h-full" data-testid="contact-info-card">
                <div className="eyebrow text-[hsl(var(--c-mango-yellow))]">Get In Touch</div>
                <h3 className="mt-3 font-heading text-2xl sm:text-3xl font-extrabold leading-tight">Based in West Palm Beach, Florida.</h3>

                <div className="mt-8 space-y-5">
                  <a href={`mailto:${BRAND.contact.email}`} className="flex items-start gap-4 group btn-transition" data-testid="contact-info-email">
                    <div className="h-12 w-12 bg-[hsl(var(--c-mango-yellow))] text-[hsl(var(--c-ink))] flex items-center justify-center shrink-0"><Mail className="h-5 w-5" /></div>
                    <div>
                      <div className="eyebrow text-[hsl(var(--c-cream))]/60">Email</div>
                      <div className="font-heading text-base sm:text-lg font-bold group-hover:text-[hsl(var(--c-mango-yellow))] break-all">{BRAND.contact.email}</div>
                    </div>
                  </a>

                  <a href={BRAND.contact.phoneHref} className="flex items-start gap-4 group btn-transition" data-testid="contact-info-phone">
                    <div className="h-12 w-12 bg-[hsl(var(--c-mango-orange))] text-white flex items-center justify-center shrink-0"><Phone className="h-5 w-5" /></div>
                    <div>
                      <div className="eyebrow text-[hsl(var(--c-cream))]/60">Phone</div>
                      <div className="font-heading text-lg font-bold group-hover:text-[hsl(var(--c-mango-orange))]">{BRAND.contact.phone}</div>
                    </div>
                  </a>

                  <div className="flex items-start gap-4" data-testid="contact-info-instagram">
                    <div className="h-12 w-12 bg-[hsl(var(--c-mango-red))] text-white flex items-center justify-center shrink-0"><Instagram className="h-5 w-5" /></div>
                    <div>
                      <div className="eyebrow text-[hsl(var(--c-cream))]/60">Instagram</div>
                      <div className="text-sm text-[hsl(var(--c-cream))]/70 italic">Handle coming soon</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-testid="contact-info-location">
                    <div className="h-12 w-12 bg-[hsl(var(--c-leaf))] text-white flex items-center justify-center shrink-0"><MapPin className="h-5 w-5" /></div>
                    <div>
                      <div className="eyebrow text-[hsl(var(--c-cream))]/60">Pickup Location</div>
                      <div className="font-heading text-lg font-bold">West Palm Beach, Florida</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-testid="contact-info-hours">
                    <div className="h-12 w-12 bg-[hsl(var(--c-teal))] text-white flex items-center justify-center shrink-0"><Clock className="h-5 w-5" /></div>
                    <div>
                      <div className="eyebrow text-[hsl(var(--c-cream))]/60">Peak Season</div>
                      <div className="font-heading text-lg font-bold">May – August</div>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal className="lg:col-span-7 bg-white lg:border-l-2 lg:border-[hsl(var(--c-ink))] border-t-2 lg:border-t-0 border-[hsl(var(--c-ink))]" variant="right">
              <div className="p-7 sm:p-10">
                <SectionHeader
                  eyebrow="Inquiry Form"
                  size="xl"
                  title="Send us a message."
                  subtitle="We typically respond within one business day during peak season."
                />
                <div className="mt-8 sm:mt-10">
                  <ContactForm defaultSubject={subject} sourcePage="contact" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
